import { useCallback, useMemo } from "react";

import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useIsClient } from "usehooks-ts";

import { useCommandMenu } from "@/components/command-menu/useCommandMenu";
import { Sidebar } from "@/components/sidebar";

import DarkThemeIcon from "./DarkTheme.svg";
import LightThemeIcon from "./LightTheme.svg";

export function ThemeSwitcher() {
  const t = useTranslations("sidebar");
  const { theme, systemTheme, setTheme } = useTheme();
  const currentTheme = theme === "system" ? systemTheme : theme;
  const isClient = useIsClient();

  const icon = useMemo(
    () =>
      currentTheme === "dark" ? (
        <DarkThemeIcon className="sm:w-[28px] sm:h-[28px]" />
      ) : (
        <LightThemeIcon className="sm:w-[28px] sm:h-[28px]" />
      ),
    [currentTheme],
  );

  const toggleTheme = useCallback(() => {
    if (
      (theme === "system" && systemTheme === "dark") ||
      (theme === "dark" && systemTheme === "dark")
    ) {
      setTheme("light");
    } else if (
      (theme === "system" && systemTheme === "light") ||
      (theme === "light" && systemTheme === "light")
    ) {
      setTheme("dark");
    } else {
      setTheme("system");
    }
  }, [theme, systemTheme, setTheme]);

  const toggleThemeLabel = currentTheme === "dark" ? t("theme.darkTheme") : t("theme.lightTheme");
  const toggleThemeTitle = t("theme.toggle");

  useCommandMenu(
    () => ({
      id: "color-theme",
      icon,
      name: toggleThemeTitle,
      onSelect: toggleTheme,
    }),
    [toggleTheme, icon, toggleThemeTitle],
  );

  if (!isClient) {
    return null;
  }

  return (
    <Sidebar.Button
      onClick={toggleTheme}
      className="rounded-8"
      icon={icon}
      title={toggleThemeLabel}
      hintKey="theme"
    />
  );
}
